import { useEffect, useRef } from "react";

import { useLocale } from "@/i18n/locale";
import { ui } from "@/i18n/ui";
import { useConsent } from "@/lib/consent-context";

export function CookieBanner() {
  const { t } = useLocale();
  const { isAsking, accept, decline } = useConsent();
  const acceptRef = useRef<HTMLButtonElement>(null);
  const wasAsking = useRef(false);

  // Move focus into the banner only when it is REOPENED from the footer — a
  // deliberate action whose result is off-screen at the bottom of the page.
  // Stealing focus on first load would yank it away from someone who is
  // already reading or tabbing.
  useEffect(() => {
    if (isAsking && wasAsking.current) acceptRef.current?.focus();
    wasAsking.current = isAsking;
  }, [isAsking]);

  if (!isAsking) return null;

  return (
    <div
      role="dialog"
      // Non-modal: it does not trap focus or block the page. Reading the site
      // without answering is a legitimate choice.
      aria-modal="false"
      aria-labelledby="cookie-title"
      aria-describedby="cookie-message"
      className="fixed inset-x-0 bottom-0 z-[70] border-t border-border bg-surface/95 backdrop-blur-md"
    >
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-4 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <h2 id="cookie-title" className="label text-accent">
            {t(ui.cookieTitle)}
          </h2>
          <p id="cookie-message" className="max-w-2xl text-sm leading-relaxed text-muted">
            {t(ui.cookieMessage)}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={decline}
            className="rounded-sm border border-border px-4 py-2 text-sm text-muted transition-colors hover:border-border-strong hover:text-text"
          >
            {t(ui.cookieDecline)}
          </button>
          <button
            ref={acceptRef}
            type="button"
            onClick={accept}
            className="rounded-sm border border-accent bg-accent px-4 py-2 text-sm font-medium text-bg transition-opacity hover:opacity-90"
          >
            {t(ui.cookieAccept)}
          </button>
        </div>
      </div>
    </div>
  );
}
